import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, ChevronRight, RefreshCw } from 'lucide-react';
import useNutritionReadiness from '../hooks/useNutritionReadiness';
import { setupCopyFor } from '../utils/nutritionReadiness';

/**
 * Shown in place of macro/calorie numbers whenever the screen doesn't have
 * real, personalized nutrition targets to show. Never renders a hardcoded
 * fallback (e.g. 2000/150/200/70) as if it were the user's own.
 *
 * It does not assume the user needs setup: the backend's nutrition readiness
 * (GET /profile/nutrition-readiness) says WHY targets are missing, and only a
 * genuinely missing goal / profile input / manual target routes into setup.
 * A failed or mismatched load shows "We couldn't load your nutrition targets"
 * with Retry, so an already-configured user is never sent through setup.
 *
 * contractUnknown: the screen's own response didn't say whether targets are
 *   ready (e.g. an older planning-summary contract) -> load error, not setup.
 * onRefresh: refetch the screen's own data (used once if readiness says the
 *   targets are actually ready, and by Retry).
 */
function NutritionTargetsSetupPrompt({ compact = false, className = '', contractUnknown = false, onRefresh = null }) {
  const navigate = useNavigate();
  const { status, readiness, reload } = useNutritionReadiness(!contractUnknown);
  const refreshedRef = useRef(false);
  const step = readiness ? readiness.next_step : null;

  // Readiness says the targets exist but this screen didn't get them: refetch
  // the screen's data once before showing a load error.
  useEffect(() => {
    if (step === 'ready' && onRefresh && !refreshedRef.current) {
      refreshedRef.current = true;
      onRefresh();
    }
  }, [step, onRefresh]);

  const boxClass =
    'w-full flex items-center justify-between gap-3 bg-card border border-border/40 rounded-xl text-left ' +
    (compact ? 'p-3' : 'p-4') + ' ' + className;

  const icon = (Icon) => (
    <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
      <Icon size={16} className="text-accent" />
    </div>
  );

  const unavailable = (detail) => (
    <div className={boxClass} data-testid="nutrition-targets-unavailable">
      <div className="flex items-center gap-3">
        {icon(Target)}
        <div>
          <p className="text-sm font-medium">We couldn&apos;t load your nutrition targets</p>
          <p className="text-xs text-muted-foreground">{detail}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          if (onRefresh) onRefresh();
          if (!contractUnknown) reload();
        }}
        className="flex items-center gap-1 text-xs font-medium text-accent flex-shrink-0"
        data-testid="nutrition-targets-retry"
      >
        <RefreshCw size={14} /> Retry
      </button>
    </div>
  );

  if (contractUnknown || status === 'error') {
    return unavailable('Check your connection and try again.');
  }
  if (status === 'loading') {
    return (
      <div className={boxClass} data-testid="nutrition-targets-checking">
        <div className="flex items-center gap-3">
          {icon(Target)}
          <p className="text-sm text-muted-foreground">Checking your nutrition targets…</p>
        </div>
      </div>
    );
  }
  if (step === 'ready') {
    return unavailable('Your targets are saved — tap Retry to load them.');
  }
  if (step === 'recover') {
    return (
      <button
        type="button"
        onClick={() => navigate('/settings?setup=recover')}
        data-testid="nutrition-targets-recover"
        className={boxClass + ' hover:border-accent/50 transition-colors'}
      >
        <div className="flex items-center gap-3">
          {icon(Target)}
          <div>
            <p className="text-sm font-medium">We couldn&apos;t load your nutrition targets</p>
            <p className="text-xs text-muted-foreground">Open Settings to restore them.</p>
          </div>
        </div>
        <ChevronRight size={16} className="text-muted-foreground flex-shrink-0" />
      </button>
    );
  }

  const copy = setupCopyFor(readiness);
  if (!copy) return unavailable('Check your connection and try again.');
  return (
    <button
      type="button"
      onClick={() => navigate('/settings?setup=' + step)}
      data-testid="nutrition-targets-setup-prompt"
      className={boxClass + ' hover:border-accent/50 transition-colors'}
    >
      <div className="flex items-center gap-3">
        {icon(Target)}
        <div>
          <p className="text-sm font-medium">{copy.title}</p>
          <p className="text-xs text-muted-foreground">{copy.body}</p>
        </div>
      </div>
      <ChevronRight size={16} className="text-muted-foreground flex-shrink-0" />
    </button>
  );
}

export default NutritionTargetsSetupPrompt;
